import { FullStory, init as initFullStorySdk } from '@fullstory/browser';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { useIdentityStore } from '@/stores/identity';
import { isE2E } from '@/utils/e2e';

const EVENTS_TO_INIT = ['click', 'keydown'];

export const useFullstoryStore = defineStore('fullstory', () => {
  const identityStore = useIdentityStore();

  const isFullStoryEnabled = env.fullstoryEnabled && !isDatadogSyntheticsBrowser && !isE2E();
  const ready = ref(false);

  function excludeIdentityFromFullstory() {
    return identityStore?.identity?.email?.endsWith('@dashsocial.com');
  }

  function initializeFullStory() {
    if (!ready.value) {
      initFullStorySdk({
        orgId: env.fullstoryOrg,
      });
      ready.value = true;
      EVENTS_TO_INIT.forEach((type) => window.removeEventListener(type, initializeFullStory));
    }
  }

  function init() {
    if (isFullStoryEnabled && !excludeIdentityFromFullstory()) {
      EVENTS_TO_INIT.forEach((type) => window.addEventListener(type, initializeFullStory));
    }
  }

  function getFullStoryUrl() {
    if (!ready.value) {
      return '';
    }

    try {
      return FullStory('getSession', { format: 'url.now' }) || 'Current session URL API not ready';
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      return `Unable to get url: ${reason}`;
    }
  }

  function sendFullstoryEvent(type, payload = {}) {
    if (ready.value) {
      FullStory('trackEvent', { name: type, properties: payload });
    }
  }

  function setIdentity(properties) {
    if (properties) {
      FullStory('setIdentity', { uid: String(properties.id), properties });
    }
  }

  return {
    init,
    ready,
    isFullStoryEnabled,
    sendFullstoryEvent,
    getFullStoryUrl,
    setIdentity,
  };
});
